import { supabase } from '../../../shared/lib/supabase';
import type {
  BillingInvoice,
  BillingInvoiceCreateInput,
  BillingInvoiceLineItem,
  BillingInvoiceLineItemCreateInput,
} from './billingTypes';
import { billingCalculationService } from './billingCalculationService';

export interface BillingInvoiceRunInput {
  period_start: string;
  period_end: string;
  organisation_id?: string;
  dry_run?: boolean;
}

export interface BillingInvoiceRun {
  id: string;
  period_start: string;
  period_end: string;
  organisation_id: string | null;
  dry_run: boolean;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'partially_completed';
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

interface BillingRunExecutionResult {
  run: BillingInvoiceRun;
  invoicesCreated: number;
}

export class BillingInvoicesService {
  private invoicesTable = 'billing_invoices';
  private invoiceLineItemsTable = 'billing_invoice_line_items';
  private runsTable = 'billing_invoice_runs';

  async getInvoices(params?: {
    organisationId?: string;
    status?: BillingInvoice['status'];
    fromDate?: string;
    toDate?: string;
  }): Promise<BillingInvoice[]> {
    let query = supabase.from(this.invoicesTable).select('*').order('created_at', { ascending: false });

    if (params?.organisationId) {
      query = query.eq('organisation_id', params.organisationId);
    }
    if (params?.status) {
      query = query.eq('status', params.status);
    }
    if (params?.fromDate) {
      query = query.gte('period_start', params.fromDate);
    }
    if (params?.toDate) {
      query = query.lte('period_end', params.toDate);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching billing invoices:', error);
      throw error;
    }

    return data || [];
  }

  async getInvoice(id: string): Promise<BillingInvoice | null> {
    const { data, error } = await supabase
      .from(this.invoicesTable)
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      console.error('Error fetching invoice:', error);
      return null;
    }
    return data;
  }

  async deleteInvoice(id: string): Promise<void> {
    const { error } = await supabase.from(this.invoicesTable).delete().eq('id', id);
    if (error) {
      console.error('Error deleting invoice:', error);
      throw error;
    }
  }

  async updateInvoice(
    id: string,
    input: {
      organisation_id?: string;
      billing_plan_id?: string | null;
      invoice_type?: BillingInvoice['invoice_type'];
      period_start?: string;
      period_end?: string;
      currency?: string;
      subtotal_amount?: number;
      tax_amount?: number;
      total_amount?: number;
      amount_paid?: number;
      amount_due?: number;
      tax_rate_percent?: number;
      prices_tax_inclusive?: boolean;
      tax_label?: string | null;
      status?: BillingInvoice['status'];
      due_at?: string | null;
    }
  ): Promise<BillingInvoice> {
    const payload: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };
    if (input.organisation_id !== undefined) payload.organisation_id = input.organisation_id;
    if (input.billing_plan_id !== undefined) payload.billing_plan_id = input.billing_plan_id ?? null;
    if (input.invoice_type !== undefined) payload.invoice_type = input.invoice_type;
    if (input.period_start !== undefined) payload.period_start = input.period_start;
    if (input.period_end !== undefined) payload.period_end = input.period_end;
    if (input.currency !== undefined) payload.currency = input.currency;
    if (input.subtotal_amount !== undefined) payload.subtotal_amount = input.subtotal_amount;
    if (input.tax_amount !== undefined) payload.tax_amount = input.tax_amount;
    if (input.total_amount !== undefined) payload.total_amount = input.total_amount;
    if (input.amount_paid !== undefined) payload.amount_paid = input.amount_paid;
    if (input.amount_due !== undefined) payload.amount_due = input.amount_due;
    if (input.tax_rate_percent !== undefined) payload.tax_rate_percent = input.tax_rate_percent;
    if (input.prices_tax_inclusive !== undefined) payload.prices_tax_inclusive = input.prices_tax_inclusive;
    if (input.tax_label !== undefined) payload.tax_label = input.tax_label ?? null;
    if (input.status !== undefined) payload.status = input.status;
    if (input.due_at !== undefined) payload.due_at = input.due_at ?? null;
    if (input.status === 'issued') payload.issued_at = new Date().toISOString();
    if (input.status === 'paid') payload.paid_at = new Date().toISOString();
    const { data, error } = await supabase
      .from(this.invoicesTable)
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (error) {
      console.error('Error updating invoice:', error);
      throw error;
    }
    return data;
  }

  async getInvoiceLineItems(invoiceId: string): Promise<BillingInvoiceLineItem[]> {
    const { data, error } = await supabase
      .from(this.invoiceLineItemsTable)
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching billing invoice line items:', error);
      throw error;
    }

    return data || [];
  }

  async createRun(input: BillingInvoiceRunInput): Promise<BillingInvoiceRun> {
    const payload = {
      ...input,
      organisation_id: input.organisation_id || null,
      dry_run: input.dry_run ?? true,
      status: 'queued' as const,
    };

    const { data, error } = await supabase.from(this.runsTable).insert([payload]).select().single();

    if (error) {
      console.error('Error creating billing invoice run:', error);
      throw error;
    }

    return data;
  }

  async getRuns(limit = 20): Promise<BillingInvoiceRun[]> {
    const { data, error } = await supabase
      .from(this.runsTable)
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching billing invoice runs:', error);
      throw error;
    }

    return data || [];
  }

  private async updateRunStatus(id: string, status: BillingInvoiceRun['status'], errorMessage?: string | null): Promise<BillingInvoiceRun> {
    const { data, error } = await supabase
      .from(this.runsTable)
      .update({
        status,
        error_message: errorMessage ?? null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating billing run status:', error);
      throw error;
    }

    return data;
  }

  private buildInvoiceNumber(): string {
    const now = new Date();
    const y = now.getUTCFullYear();
    const m = String(now.getUTCMonth() + 1).padStart(2, '0');
    const d = String(now.getUTCDate()).padStart(2, '0');
    const suffix = Math.floor(Math.random() * 100000)
      .toString()
      .padStart(5, '0');
    return `KR-INV-${y}${m}${d}-${suffix}`;
  }

  private roundMoney(value: number): number {
    return Math.round(value * 100) / 100;
  }

  async createDraftInvoice(input: BillingInvoiceCreateInput): Promise<BillingInvoice> {
    const subtotalInput = Number(input.subtotal_amount || 0);
    const taxRate = Number(input.tax_rate_percent || 0);
    const pricesTaxInclusive = Boolean(input.prices_tax_inclusive);
    const totalAmountOverride = input.total_amount !== undefined ? Number(input.total_amount) : undefined;

    let subtotal = 0;
    let tax = 0;
    let total = 0;

    if (totalAmountOverride !== undefined && totalAmountOverride > 0) {
      total = this.roundMoney(totalAmountOverride);
      if (pricesTaxInclusive && taxRate > 0) {
        const preTax = total / (1 + taxRate / 100);
        subtotal = this.roundMoney(preTax);
        tax = this.roundMoney(total - preTax);
      } else if (!pricesTaxInclusive && taxRate > 0) {
        subtotal = this.roundMoney(total / (1 + taxRate / 100));
        tax = this.roundMoney(total - subtotal);
      } else {
        subtotal = total;
        tax = 0;
      }
    } else if (pricesTaxInclusive) {
      subtotal = this.roundMoney(subtotalInput);
      tax =
        input.tax_amount !== undefined
          ? this.roundMoney(Number(input.tax_amount || 0))
          : this.roundMoney((subtotal * taxRate) / 100);
      total = this.roundMoney(subtotal + tax);
    } else {
      subtotal = this.roundMoney(subtotalInput);
      tax =
        input.tax_amount !== undefined
          ? this.roundMoney(Number(input.tax_amount || 0))
          : this.roundMoney((subtotal * taxRate) / 100);
      total = this.roundMoney(subtotal + tax);
    }

    const status = (input.status ?? 'draft') as BillingInvoice['status'];
    const now = new Date().toISOString();
    const invoiceInsertPayload = {
      organisation_id: input.organisation_id,
      billing_plan_id: input.billing_plan_id || null,
      invoice_number: this.buildInvoiceNumber(),
      invoice_type: input.invoice_type,
      period_start: input.period_start,
      period_end: input.period_end,
      currency: input.currency,
      subtotal_amount: subtotal,
      tax_amount: tax,
      tax_rate_percent: taxRate,
      prices_tax_inclusive: pricesTaxInclusive,
      tax_label: input.tax_label || 'Tax',
      total_amount: total,
      amount_paid: 0,
      amount_due: total,
      status,
      due_at: input.due_at || null,
      ...(status === 'issued' && { issued_at: now }),
      ...(status === 'paid' && { issued_at: now, paid_at: now }),
    };

    const { data: invoice, error: invoiceError } = await supabase
      .from(this.invoicesTable)
      .insert([invoiceInsertPayload])
      .select()
      .single();

    if (invoiceError) {
      console.error('Error creating draft invoice:', invoiceError);
      throw invoiceError;
    }

    const lineItemsPayload: BillingInvoiceLineItemCreateInput[] = input.line_items || [];
    if (lineItemsPayload.length > 0) {
      const rows = lineItemsPayload.map((line) => ({
        invoice_id: invoice.id,
        line_type: line.line_type,
        metric: line.metric ?? null,
        description: line.description,
        quantity: line.quantity ?? null,
        unit_price: line.unit_price ?? null,
        amount: Number(line.amount || 0),
        metadata: line.metadata ?? null,
      }));

      const { error: itemsError } = await supabase.from(this.invoiceLineItemsTable).insert(rows);
      if (itemsError) {
        console.error('Error creating invoice line items:', itemsError);
        throw itemsError;
      }
    }

    return invoice;
  }

  async computeInvoiceDraftFromUsage(input: {
    organisationId: string;
    periodStartIso: string;
    periodEndIso: string;
    invoiceType: BillingInvoice['invoice_type'];
    billingPlanId?: string | null;
    currencyOverride?: string;
    taxRatePercentOverride?: number;
    pricesTaxInclusiveOverride?: boolean;
    taxLabelOverride?: string;
  }) {
    return billingCalculationService.computeInvoiceDraft(input);
  }

  async executeRun(runId: string): Promise<BillingRunExecutionResult> {
    const { data: run, error } = await supabase
      .from(this.runsTable)
      .select('*')
      .eq('id', runId)
      .single();

    if (error) {
      console.error('Error fetching billing run:', error);
      throw error;
    }

    await this.updateRunStatus(run.id, 'running');

    try {
      if (!run.organisation_id) {
        const completed = await this.updateRunStatus(run.id, 'failed', 'Run execution requires organisation_id.');
        return { run: completed, invoicesCreated: 0 };
      }

      const computed = await billingCalculationService.computeInvoiceDraft({
        organisationId: run.organisation_id,
        periodStartIso: run.period_start,
        periodEndIso: run.period_end,
        invoiceType: 'usage_settlement_end',
      });

      let invoicesCreated = 0;
      if (!run.dry_run) {
        await this.createDraftInvoice(computed.draftInput);
        invoicesCreated = 1;
      }

      const completed = await this.updateRunStatus(run.id, 'completed', null);
      return { run: completed, invoicesCreated };
    } catch (runError: unknown) {
      const message = runError instanceof Error ? runError.message : 'Run execution failed.';
      const failed = await this.updateRunStatus(run.id, 'failed', message);
      return { run: failed, invoicesCreated: 0 };
    }
  }

  async executeQueuedRuns(limit = 10): Promise<BillingRunExecutionResult[]> {
    const { data, error } = await supabase
      .from(this.runsTable)
      .select('*')
      .eq('status', 'queued')
      .order('created_at', { ascending: true })
      .limit(limit);

    if (error) {
      console.error('Error fetching queued billing runs:', error);
      throw error;
    }

    const queuedRuns = data || [];
    const results: BillingRunExecutionResult[] = [];
    for (const run of queuedRuns) {
      const result = await this.executeRun(run.id);
      results.push(result);
    }
    return results;
  }
}

export const billingInvoicesService = new BillingInvoicesService();
